import { useSelector } from "react-redux"
import { selectIsIssuancePolicy, selectPolicyDataPageModalType, selectPolicyNumber, selectPolicyType, selectProposalIdForPolicy } from "../../features/ebam/plicy-data-page/policy-data-page.slice"
import { selectPolicyAmountInsurance, selectPolicyCoveragePeriod, selectPolicyCPDType, selectPolicyDueDates, selectPolicyDueDatesInfo, selectPolicyRefundOfPremiums, selectPolicyTerminationsAge } from "../../features/ebam/plicy-data-page/policy-coverage-premium-details/policy-coverage-premium-details.slice";
import { selectPolicyAllowedNextStatus, selectPolicyCityMunicipalityProvince, selectPolicyContribution, selectPolicyDocumentCode, selectPolicyEffectiveDate, selectPolicyEligibleIndividuals, selectPolicyInformationType, selectPolicyParticipationRequirements, selectPolicyUnderwritingProvisions, selectPolicyUnderwritingProvisionsNotes, selectSpecialUnderwritingProvision } from "../../features/ebam/plicy-data-page/policy-information/policy-information.slice";
import { selectFaceAmountOfInsurance, selectNonCoverageProvisions, selectPolicyClaimsProcedure, selectPolicyEnrollment, selectPolicyGeneralDefinitions, selectPolicyRollOverProvisions, selectPolicySpecialProvisions, selectPolicySpecialUnderwritingProvisionsType, selectPolicyTerminationOfIndividual, selectPremiumComputation } from "../../features/ebam/plicy-data-page/policy-special-underwriting-provisions/policy-special-underwriting-provisions.slice";
import { selectIsEditingTAT, selectPolicyTATModalType, selectTATItems } from "../../features/ebam/plicy-data-page/policy-tat/policy-tat.slice";
import { PolicyContractType } from "../../api/ebam/policy/policy-api";
import { policyDataPageType } from "../../constant/ebam-constant";

export const usePolicyDataState = () => {
    
    // MAIN POLICY
    const policyNumber = useSelector(selectPolicyNumber);
    const policyDataPageModalType = useSelector(selectPolicyDataPageModalType);
    const isPolicyDataPageGCLIP = policyDataPageModalType === 'policyDataPageGCLIP'

    const typeOfPolicy = useSelector(selectPolicyType);
    const isPolicyTypeGCLIP_PRIN = typeOfPolicy === policyDataPageType.gcli_prin
    const isPolicyTypeGCLIP_OUT = typeOfPolicy === policyDataPageType.gcli_out
    const isPolicyTypeGPA_GADDP = typeOfPolicy === policyDataPageType.gpa_gaddp

    const contractType: PolicyContractType | undefined =
            isPolicyTypeGCLIP_OUT ? "outstanding" : 
                isPolicyTypeGCLIP_PRIN ? "principal" : 
                    isPolicyTypeGPA_GADDP ? "gpa" : 
                        undefined;
    const contractTypeOutstanding = contractType === 'outstanding';
    const contractTypePrincipal = contractType === 'principal';
    const contractTypeGPA = contractType === 'gpa';

    const policyId = useSelector(selectProposalIdForPolicy);

    // POLICY COVERAGE AND PREMIUM
    const policyCoveragePremium = useSelector(selectPolicyCPDType);
    const isPCPAmountOfInsurance = policyCoveragePremium === 'amountOfInsurance'
    const isPCPCoveragePeriod = policyCoveragePremium === 'coveragePeriod'
    const isPCPDueDates = policyCoveragePremium === 'dueDates'
    const isPCPRefundOfPremiums = policyCoveragePremium === 'refundOfPremiums'
    const isPCPTerminationAge = policyCoveragePremium === 'terminationAge'
    const isPCPDueDatesInfo = policyCoveragePremium === 'dueDatesInfo'
    const policyAmountInsurance = useSelector(selectPolicyAmountInsurance);
    const policyCoveragePeriod = useSelector(selectPolicyCoveragePeriod);
    const policyDueDates = useSelector(selectPolicyDueDates);
    const policyDueDatesInfo = useSelector(selectPolicyDueDatesInfo);
    const policyRefundOfPremiums = useSelector(selectPolicyRefundOfPremiums);
    const policyTerminationAge = useSelector(selectPolicyTerminationsAge);

    // POLICY INFORMATION
    const policyInformationType = useSelector(selectPolicyInformationType);
    const isPIEligibleIndividuals =  policyInformationType === 'eligibleIndividuals'
    const isPIContribution =  policyInformationType === 'contribution'
    const isPIParticipationRequirements =  policyInformationType === 'participationRequirements'
    const isPIUnderwritingProvisions =  policyInformationType === 'underwritingProvisions'
    const isPIUnderwritingProvisionsNotes =  policyInformationType === 'underwritingProvisionsNotes'
    const isPIFullAddress =  policyInformationType === 'cityMunicipalityProvince'
    const isPISpecialUnderwritingProvisions =  policyInformationType === 'specialUnderwritingProvisions'
    const isPIDocumentCode =  policyInformationType === 'docCode'
    const policyEligibleIndividual = useSelector(selectPolicyEligibleIndividuals);
    const policyContribution = useSelector(selectPolicyContribution);
    const policyParticipationRequirements = useSelector(selectPolicyParticipationRequirements);
    const policyUnderwritingProvisions = useSelector(selectPolicyUnderwritingProvisions);
    const policyUnderwritingProvisionsNotes = useSelector(selectPolicyUnderwritingProvisionsNotes);
    const policySpecialUnderwritingProvisions = useSelector(selectSpecialUnderwritingProvision);
    const policyEffectiveDate = useSelector(selectPolicyEffectiveDate);
    
    const policyFullAddress = useSelector(selectPolicyCityMunicipalityProvince);
    const policyDocumentCode = useSelector(selectPolicyDocumentCode);
    const policyAllowedNextStatus = useSelector(selectPolicyAllowedNextStatus);

    // POLICY SPECIAL UNDERWRITING PROVISIONS
    const policySpecialUnderwritingProvisionsType = useSelector(selectPolicySpecialUnderwritingProvisionsType);
    const isPSUPEnrollment = policySpecialUnderwritingProvisionsType === 'enrollment'
    const isPSUPRollOverProvisions = policySpecialUnderwritingProvisionsType === 'rollOverProvisions'
    const isPSUPTerminationOfIndividual = policySpecialUnderwritingProvisionsType === 'terminationOfIndividual'
    const isPSUPGeneralDefinitions = policySpecialUnderwritingProvisionsType === 'generalDefinitions'
    const isPSUPFaceAmountOfInsurance = policySpecialUnderwritingProvisionsType === 'faceAmountOfInsurance'
    const isPSUPPremiumComputation = policySpecialUnderwritingProvisionsType === 'premiumComputation'
    const isPSUPNonCoverageProvisions = policySpecialUnderwritingProvisionsType === 'nonCoverageProvisions'
    const isPSUPClaimsProcedure = policySpecialUnderwritingProvisionsType === 'claimsProcedure'
    const isPSUPSpecialProvisions = policySpecialUnderwritingProvisionsType === 'specialProvisions'
    const policyEnrollment = useSelector(selectPolicyEnrollment)
    const policyRollOverProvisions = useSelector(selectPolicyRollOverProvisions)
    const policyTerminationOfIndividual = useSelector(selectPolicyTerminationOfIndividual)
    const policyFaceAmountOfInsurance = useSelector(selectFaceAmountOfInsurance)
    const policyGeneralDefinitions = useSelector(selectPolicyGeneralDefinitions)
    const policyPremiumComputation = useSelector(selectPremiumComputation)
    const policyCoverageProvisions = useSelector(selectNonCoverageProvisions)
    const policyClaimsProcedure = useSelector(selectPolicyClaimsProcedure)
    const policySpecialProvisions = useSelector(selectPolicySpecialProvisions)

    // POLICY TAT
    const policyIsEditingTAT = useSelector(selectIsEditingTAT);
    const policyTATModalType = useSelector(selectPolicyTATModalType);
    const policyTATItems = useSelector(selectTATItems);

    const isForIssuancePolicy = useSelector(selectIsIssuancePolicy);
    const isShowModalForSubmitIssuance = isForIssuancePolicy.isIssuancePolicy
    const notesForSubmitPolicyContract = isForIssuancePolicy.notesForPolicyApproval
    
    return {
        // MAIN POLICY
        policyNumber,
        policyDataPageModalType,
        isPolicyDataPageGCLIP,
        typeOfPolicy,
        isPolicyTypeGCLIP_PRIN,
        isPolicyTypeGCLIP_OUT,
        isPolicyTypeGPA_GADDP,
        contractType,
        contractTypeOutstanding,
        contractTypePrincipal,
        contractTypeGPA,

        policyId,

        // POLICY COVERAGE AND PREMIUM
        policyCoveragePremium,
        isPCPAmountOfInsurance,
        isPCPCoveragePeriod,
        isPCPDueDates,
        isPCPRefundOfPremiums,
        isPCPTerminationAge,
        isPCPDueDatesInfo,
        policyAmountInsurance,
        policyCoveragePeriod,
        policyDueDates,
        policyDueDatesInfo,
        policyRefundOfPremiums,
        policyTerminationAge,
        // POLICY INFORMATION
        policyInformationType,
        isPIEligibleIndividuals,
        isPIContribution,
        isPIParticipationRequirements,
        isPIUnderwritingProvisions,
        isPIUnderwritingProvisionsNotes,
        isPIFullAddress,
        isPISpecialUnderwritingProvisions,
        isPIDocumentCode,
        policyEligibleIndividual,
        policyContribution,
        policyParticipationRequirements,
        policyUnderwritingProvisions,
        policyUnderwritingProvisionsNotes,
        policySpecialUnderwritingProvisions,
        policyEffectiveDate,

        policyFullAddress,
        policyDocumentCode,
        policyAllowedNextStatus,

        // POLICY SPECIAL UNDERWRITING PROVISIONS
        policySpecialUnderwritingProvisionsType,
        isPSUPEnrollment,
        isPSUPRollOverProvisions,
        isPSUPTerminationOfIndividual,
        isPSUPGeneralDefinitions,
        isPSUPFaceAmountOfInsurance,
        isPSUPPremiumComputation,
        isPSUPNonCoverageProvisions,
        isPSUPClaimsProcedure,
        isPSUPSpecialProvisions,
        policyEnrollment,
        policyRollOverProvisions,
        policyTerminationOfIndividual,
        policyFaceAmountOfInsurance,
        policyGeneralDefinitions,
        policyPremiumComputation,
        policyCoverageProvisions,
        policyClaimsProcedure,
        policySpecialProvisions,
        // POLICY TAT
        policyIsEditingTAT,
        policyTATModalType,
        policyTATItems,

        isForIssuancePolicy,
        isShowModalForSubmitIssuance,
        notesForSubmitPolicyContract,
    }
}